import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Paper, 
  TextField, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  Button, 
  Box, 
  Alert, 
  CircularProgress, 
  Divider,
  IconButton,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { api } from '../../services/api';
import { useConfirm, useToast } from '../../contexts/ConfirmContext';

interface FormData {
  instructorId: string;
  date: Date;
  time: Date;
  duration: number;
  notes: string;
}

const BookLessonPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialDate = queryParams.get('date');

  const [formData, setFormData] = useState<FormData>({
    instructorId: '',
    date: initialDate ? parseISO(initialDate) : new Date(),
    time: new Date(),
    duration: 60, // Default 1 hour
    notes: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [bookedLessons, setBookedLessons] = useState<any[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<any | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [assignedInstructorName, setAssignedInstructorName] = useState<string | null>(null);
  const [topics, setTopics] = useState<any[]>([]);
  const [selectedTopicId, setSelectedTopicId] = useState<number | "">("");
  const [completedTopicIds, setCompletedTopicIds] = useState<Set<number>>(new Set());
  const [freeLessonCredits, setFreeLessonCredits] = useState<number>(0);
  const [useFreeLessonCredit, setUseFreeLessonCredit] = useState(false);

  const confirm = useConfirm();
  const showToast = useToast();

  const mergedSlots = useMemo(() => {
    const slots = availableSlots.map(slot => ({
      ...slot,
      type: 'available' as const
    }));
    
    const lessons = bookedLessons.map(lesson => {
      const start = new Date(lesson.lessonDate);
      const end = new Date(start.getTime() + lesson.duration * 60 * 1000);
      return {
        id: lesson.id,
        startTime: lesson.lessonDate,
        endTime: end.toISOString(),
        duration: lesson.duration,
        type: 'booked' as const,
        isApproved: lesson.isApproved,
        completed: lesson.completed,
        notes: lesson.notes || '',
        topicId: lesson.topicId || '',
        topicName: lesson.topicName,
        topicCode: lesson.topicCode,
        instructorName: lesson.instructorName
      };
    });
    
    return [...slots, ...lessons].sort((a, b) => 
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
  }, [availableSlots, bookedLessons]);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const response = await api.getTopics();
        setTopics(response.data);
      } catch (err) {
        console.error("Error loading topics:", err);
      }
    };
    fetchTopics();
  }, []);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const response = await api.getStudentLessonsProgress();
        const progressData = response.data;
        if (progressData && Array.isArray(progressData.lessons)) {
          const completed = new Set<number>();
          progressData.lessons.forEach((lesson: any) => {
            if (lesson.isCompleted && lesson.topicId) {
              completed.add(lesson.topicId);
            }
          });
          setCompletedTopicIds(completed);
        }
      } catch (err) {
        console.error("Error loading student progress status:", err);
      }
    };
    fetchProgress();
  }, []);

  useEffect(() => {
    const fetchAssignedInstructor = async () => {
      try {
        setLoading(true);
        const response = await api.getStudentProfile();
        const profile = response.data;
        
        if (profile.instructorId) {
          setFormData(prev => ({
            ...prev,
            instructorId: profile.instructorId
          }));
          setAssignedInstructorName(profile.instructorName || 'Assigned Instructor');
          setFreeLessonCredits(profile.freeLessonCredits || 0);
        } else {
          setError('No instructor has been assigned to you yet. Please contact support.');
        }
        
        setLoading(false);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load instructor details');
        setLoading(false);
      }
    };

    fetchAssignedInstructor();
  }, []);

  const fetchAvailableSlots = useCallback(async () => {
    try {
      setError(null);
      const dateStr = format(formData.date, 'yyyy-MM-dd');
      
      const [availableRes, lessonsRes] = await Promise.all([
        api.get(`/students/available-slots?date=${dateStr}`),
        api.getStudentLessons(dateStr, dateStr)
      ]);
      
      setAvailableSlots(availableRes.data);
      setBookedLessons(lessonsRes.data);

      const localMerged = [
        ...availableRes.data.map((s: any) => ({ ...s, type: 'available' as const })),
        ...lessonsRes.data.map((l: any) => {
          const start = new Date(l.lessonDate);
          const end = new Date(start.getTime() + l.duration * 60 * 1000);
          return {
            id: l.id,
            startTime: l.lessonDate,
            endTime: end.toISOString(),
            duration: l.duration,
            type: 'booked' as const,
            isApproved: l.isApproved,
            completed: l.completed,
            notes: l.notes || '',
            topicId: l.topicId || '',
            topicName: l.topicName,
            topicCode: l.topicCode,
            instructorName: l.instructorName
          };
        })
      ].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

      // Try auto-selecting slot matching initialDate redirect parameter
      if (initialDate) {
        const matchingSlot = localMerged.find((slot: any) => 
          format(new Date(slot.startTime), 'yyyy-MM-dd HH:mm') === format(parseISO(initialDate), 'yyyy-MM-dd HH:mm')
        );
        if (matchingSlot) {
          setSelectedSlot(matchingSlot);
          setFormData(prev => ({
            ...prev,
            time: new Date(matchingSlot.startTime),
            duration: matchingSlot.duration || 60,
            notes: matchingSlot.type === 'booked' ? (matchingSlot.notes || '') : ''
          }));
          setSelectedTopicId(matchingSlot.type === 'booked' ? (matchingSlot.topicId || '') : '');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load time slots or appointments');
    }
  }, [formData.date, initialDate]);

  useEffect(() => {
    if (formData.instructorId && formData.date) {
      fetchAvailableSlots();
    }
  }, [formData.instructorId, formData.date, fetchAvailableSlots]);

  const handleDateChange = (date: Date | null) => {
    if (!date) return;
    setSelectedSlot(null); // Reset selected slot when date changes
    setFormData(prev => ({
      ...prev,
      date,
      notes: ''
    }));
    setSelectedTopicId('');
  };

  const handleSlotSelect = (slot: any) => {
    setSelectedSlot(slot);
    setFormData(prev => ({
      ...prev,
      date: new Date(slot.startTime),
      time: new Date(slot.startTime),
      duration: slot.duration || 60,
      notes: slot.type === 'booked' ? (slot.notes || '') : ''
    }));
    setSelectedTopicId(slot.type === 'booked' ? (slot.topicId || '') : '');
  };

  const handleCancelLesson = async () => {
    if (!selectedSlot || selectedSlot.type !== 'booked') return;

    const confirmed = await confirm({
      title: 'Cancel Reservation',
      message: 'Are you sure you want to cancel this lesson request? This action cannot be undone.',
      confirmText: 'Yes, Cancel',
      cancelText: 'Cancel',
      severity: 'danger'
    });

    if (!confirmed) return;

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const response = await api.cancelLesson(selectedSlot.id);

      if (response.data.success) {
        showToast('Appointment cancelled successfully.', 'success');
        setSuccess('Appointment cancelled successfully.');
        setSelectedSlot(null);
        fetchAvailableSlots();
      } else {
        setError(response.data.message || 'Failed to cancel appointment.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred while cancelling the appointment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedSlot) return;
    
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);
      
      if (selectedSlot.type === 'booked') {
        await api.updateStudentLesson(selectedSlot.id, {
          topicId: selectedTopicId !== "" ? (selectedTopicId as number) : undefined,
          notes: formData.notes
        });
        showToast('Reservation updated successfully.', 'success');
        setSuccess('Reservation updated successfully.');
        setTimeout(() => {
          setSuccess(null);
          fetchAvailableSlots();
          setSelectedSlot(null);
        }, 2000);
      } else {
        // Combine date and time
        const startTime = new Date(formData.date);
        startTime.setHours(formData.time.getHours(), formData.time.getMinutes());
        
        const endTime = new Date(startTime);
        endTime.setMinutes(endTime.getMinutes() + formData.duration);
        
        await api.bookLesson({
          instructorId: formData.instructorId,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          duration: formData.duration,
          notes: formData.notes,
          topicId: selectedTopicId !== "" ? selectedTopicId : undefined,
          useFreeLessonCredit: useFreeLessonCredit
        });
        
        showToast('Lesson request created successfully! It will be finalized after instructor approval.', 'success');
        setSuccess('Lesson request created successfully! It will be finalized after instructor approval.');
        setTimeout(() => navigate('/student/dashboard'), 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred while processing the request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress size={50} />
          <Typography variant="body1" color="textSecondary">Loading instructor details...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e', mb: 3 }}>
        Book a Driving Lesson
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          {success}
        </Alert>
      )}
      
      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.05)' }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr' }, gap: 5 }}>
            {/* Left Column: Form Fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Your Instructor"
                value={assignedInstructorName || 'Loading...'}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
              />
              
              {selectedSlot ? (
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: selectedSlot.type === 'booked' 
                      ? (selectedSlot.isApproved ? 'rgba(46, 125, 50, 0.02)' : 'rgba(237, 108, 2, 0.02)') 
                      : 'rgba(25, 118, 210, 0.02)', 
                    borderColor: selectedSlot.type === 'booked' 
                      ? (selectedSlot.isApproved ? '#2e7d32' : '#ed6c02') 
                      : '#1976d2', 
                    borderRadius: 3 
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    color={selectedSlot.type === 'booked' ? (selectedSlot.isApproved ? 'success.main' : 'warning.main') : 'primary'} 
                    fontWeight="bold" 
                    sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    {selectedSlot.type === 'booked' ? '📝 Reservation Details' : '🎯 Selected Lesson Details'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                    <strong>Date:</strong> {format(new Date(selectedSlot.startTime), 'dd MMMM yyyy EEEE', { locale: enUS })}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                    <strong>Time:</strong> {format(new Date(selectedSlot.startTime), 'HH:mm')} - {format(new Date(selectedSlot.endTime), 'HH:mm')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                    <strong>Duration:</strong> {selectedSlot.duration || 60} Minutes
                  </Typography>
                  {selectedSlot.type === 'booked' && (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                      <strong>Status:</strong> {selectedSlot.isApproved ? 'Approved' : 'Pending Approval'}
                    </Typography>
                  )}
                </Paper>
              ) : (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  Please select a lesson slot or your reservation from the right panel.
                </Alert>
              )}

              {freeLessonCredits > 0 && selectedSlot && selectedSlot.type !== 'booked' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useFreeLessonCredit}
                      onChange={(e) => setUseFreeLessonCredit(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Redeem Free Lesson Credit ({freeLessonCredits} available)
                    </Typography>
                  }
                />
              )}

              <FormControl fullWidth>
                <InputLabel id="topic-label">Lesson Topic (DVSA Syllabus)</InputLabel>
                <Select
                  labelId="topic-label"
                  id="topicId"
                  value={selectedTopicId}
                  onChange={(e) => setSelectedTopicId(e.target.value as number | "")}
                  label="Lesson Topic (DVSA Syllabus)"
                  renderValue={(selected: any) => {
                    if (selected === "" || !selected) {
                      return <em>No Topic (Free Practice)</em>;
                    }
                    const topic = topics.find(t => t.id === selected);
                    if (!topic) return "";
                    const isCompleted = completedTopicIds.has(topic.id);
                    return (
                      <Box component="span" sx={{ 
                        color: isCompleted ? '#2e7d32' : 'inherit', 
                        fontWeight: isCompleted ? 'bold' : 'normal',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <strong>{topic.code}</strong> - {topic.name} {isCompleted ? '✓ (Completed)' : ''}
                      </Box>
                    );
                  }}
                >
                  <MenuItem value=""><em>No Topic (Free Practice)</em></MenuItem>
                  {topics.map((t) => {
                    const isCompleted = completedTopicIds.has(t.id);
                    return (
                      <MenuItem 
                        key={t.id} 
                        value={t.id}
                        sx={{
                          color: isCompleted ? '#2e7d32' : 'inherit',
                          fontWeight: isCompleted ? 'medium' : 'normal',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%'
                        }}
                      >
                        <Box>
                          <strong>{t.code}</strong> - {t.name}
                        </Box>
                        {isCompleted && (
                          <Box component="span" sx={{ 
                            ml: 1, 
                            fontSize: '0.75rem', 
                            backgroundColor: 'rgba(46, 125, 50, 0.1)', 
                            color: '#2e7d32', 
                            px: 1.2, 
                            py: 0.3, 
                            borderRadius: 1,
                            fontWeight: 'bold',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}>
                            ✓ Completed
                          </Box>
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              
              <TextField
                id="notes"
                name="notes"
                label="Notes for Instructor"
                placeholder="Any notes or requests for the lesson..."
                value={formData.notes}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                  setFormData(prev => ({ ...prev, notes: e.target.value }))
                }
                fullWidth
                multiline
                rows={3}
              />
              
              <Box mt={2} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                {selectedSlot && selectedSlot.type === 'booked' ? (
                  <>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleCancelLesson}
                      disabled={submitting}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        borderColor: '#d32f2f',
                        '&:hover': {
                          borderColor: '#b71c1c',
                          backgroundColor: 'rgba(211, 47, 47, 0.04)'
                        }
                      }}
                    >
                      {submitting ? <CircularProgress size={24} color="inherit" /> : 'Cancel / Delete Request'}
                    </Button>
                    
                    <Button
                      type="submit"
                      variant="contained"
                      color="secondary"
                      disabled={submitting}
                      sx={{ 
                        minWidth: 180, 
                        py: 1.5, 
                        borderRadius: 2, 
                        fontWeight: 'bold',
                        boxShadow: '0 4px 12px rgba(156, 39, 176, 0.2)'
                      }}
                    >
                      {submitting ? <CircularProgress size={24} color="inherit" /> : 'Update Reservation'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={submitting || !formData.instructorId || !selectedSlot}
                      sx={{ 
                        minWidth: 180, 
                        py: 1.5, 
                        borderRadius: 2, 
                        fontWeight: 'bold',
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
                      }}
                    >
                      {submitting ? <CircularProgress size={24} color="inherit" /> : 'Send Appointment Request'}
                    </Button>
                  </>
                )}
              </Box>
            </Box>
            
            {/* Right Column: Available Time Slots */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#37474f', mb: 2 }}>
                Available Time Slots
              </Typography>
              
              <Box display="flex" justifyContent="center" alignItems="center" gap={1} sx={{ mb: 3 }}>
                <IconButton 
                  onClick={() => {
                    const prev = new Date(formData.date);
                    prev.setDate(prev.getDate() - 1);
                    handleDateChange(prev);
                  }}
                  disabled={format(formData.date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={formData.date}
                    onChange={(date) => handleDateChange(date as Date | null)}
                    minDate={new Date()}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: 170 }
                      }
                    }}
                  />
                </LocalizationProvider>
                <IconButton 
                  onClick={() => {
                    const next = new Date(formData.date);
                    next.setDate(next.getDate() + 1);
                    handleDateChange(next);
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>

              <Divider sx={{ mb: 2 }} />
              
              {mergedSlots.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', mt: 2 }}>
                  No available slots found for the selected date. Please choose another date when your instructor is available.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 420, overflow: 'auto', pr: 1 }}>
                  {mergedSlots.map((slot) => {
                    const start = new Date(slot.startTime);
                    const end = new Date(slot.endTime);
                    const isSelected = selectedSlot && selectedSlot.id === slot.id && selectedSlot.type === slot.type;
                      
                    // Determine styling based on type and status
                    let borderStyle = '1px solid #e0e0e0';
                    let bgColor = '#fff';
                    let hoverBgColor = 'rgba(25, 118, 210, 0.02)';
                    let tagText = `${slot.duration || 60} Mins`;
                    let tagBg = 'rgba(25, 118, 210, 0.1)';
                    let tagColor = '#1976d2';
                    let subtitle = '';

                    if (slot.type === 'booked') {
                      if (slot.isApproved) {
                        borderStyle = isSelected ? '2px solid #2e7d32' : '2px dashed #2e7d32';
                        bgColor = isSelected ? 'rgba(46, 125, 50, 0.08)' : 'rgba(46, 125, 50, 0.03)';
                        hoverBgColor = 'rgba(46, 125, 50, 0.06)';
                        tagText = 'Approved';
                        tagBg = 'rgba(46, 125, 50, 0.15)';
                        tagColor = '#2e7d32';
                      } else {
                        borderStyle = isSelected ? '2px solid #ed6c02' : '2px dashed #ed6c02';
                        bgColor = isSelected ? 'rgba(237, 108, 2, 0.08)' : 'rgba(237, 108, 2, 0.03)';
                        hoverBgColor = 'rgba(237, 108, 2, 0.06)';
                        tagText = 'Pending Approval';
                        tagBg = 'rgba(237, 108, 2, 0.15)';
                        tagColor = '#ed6c02';
                      }
                      
                      subtitle = slot.topicCode 
                        ? `Lesson: ${slot.topicCode} - ${slot.topicName}` 
                        : 'Free Practice';
                    } else {
                      if (isSelected) {
                        borderStyle = '2px solid #1976d2';
                        bgColor = 'rgba(25, 118, 210, 0.04)';
                      }
                    }

                    return (
                      <Paper 
                        key={`${slot.type}-${slot.id}`} 
                        elevation={isSelected ? 4 : 1}
                        sx={{ 
                          p: 2, 
                          mb: 1.5, 
                          cursor: 'pointer',
                          borderRadius: 2,
                          border: borderStyle,
                          backgroundColor: bgColor,
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: slot.type === 'booked' ? (slot.isApproved ? '#2e7d32' : '#ed6c02') : '#1976d2',
                            backgroundColor: hoverBgColor,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                          }
                        }}
                        onClick={() => handleSlotSelect(slot)}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 'bold', 
                            color: isSelected 
                              ? (slot.type === 'booked' ? (slot.isApproved ? '#2e7d32' : '#ed6c02') : '#1976d2') 
                              : (slot.type === 'booked' ? (slot.isApproved ? '#2e7d32' : '#ed6c02') : 'inherit')
                          }}>
                            {format(start, 'HH:mm')} - {format(end, 'HH:mm')}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            px: 1.5, 
                            py: 0.5, 
                            borderRadius: 1, 
                            backgroundColor: tagBg,
                            color: tagColor,
                            fontWeight: 'bold',
                            fontSize: '0.75rem'
                          }}>
                            {tagText}
                          </Typography>
                        </Box>
                        {subtitle && (
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                            {subtitle}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default BookLessonPage;
